import Checkbox from '../../../../components/form/elements/checkbox';
import Select, { Option } from '../../../../components/form/elements/select';
import Text from '../../../../components/form/elements/text';
import Integer from '../../../../components/form/elements/integer';
import Disabled from '../../../../components/form/elements/disabled';
import File from '../../../../components/form/elements/file';
import Classifier from '../../../../interfaces/classifier';
import ClassifierPropertyOptions from '../../../../interfaces/classifier-property-options';
import * as yup from 'yup';
import { FormComponent } from '../../../../components/form';
import ClassifierValue from '../../../../interfaces/classifierValue';
import { InternalClassifierService } from '../../../../services/internal-classifier.service';
import { numberTransform, route } from '../../../../services/utils';
import {
  INTERNAL_CLASSIFIER_VALUE_EDIT,
  INTERNAL_CLASSIFIER_VALUE_VIEW,
  INTERNAL_CLASSIFIER_VALUES,
} from '../../../../router/routes';
import Decimal from '../../../../components/form/elements/decimal';
import FieldTypes from '../../../../enums/field-types';
import ClassifierProperty from '../../../../interfaces/classifierProperty';
import ClassifierPropertyKey from '../../../../enums/classifier-property-key';

export const conditionally_translatable_property_types = [
  FieldTypes.SELECT_VALUE.toString(),
  FieldTypes.MULTI_SELECT_VALUE.toString(),
  FieldTypes.SELECT_RELATION.toString(),
  FieldTypes.MULTI_SELECT_RELATION.toString(),
];
export const permanently_translatable_property_types = [
  FieldTypes.READ_ONLY.toString(),
];

export class ClassifierManager {
  protected classifier: Classifier;
  protected classifierService: InternalClassifierService;
  protected t: any;
  protected readonly: boolean;
  protected columnsOrder: string[] = [];
  protected hiddenTableColumns: string[] = [];
  protected classifierValueTitle: string = 'classifiers.value';
  protected prefillableFields: string[] = [];

  constructor(
    classifier: Classifier,
    classifierService: InternalClassifierService,
    getTranslation: (key: string) => string,
    readonly: boolean = false
  ) {
    this.classifier = classifier;
    this.classifierService = classifierService;
    this.t = getTranslation;
    this.readonly = readonly;

    switch (this.classifier.key) {
      case 'product_categories_and_types':
        this.columnsOrder = [
          'properties.sector',
          'properties.crop_group',
          'value',
          'properties.lad_code',
          'properties.lad_code_sowing',
          'properties.n_correction_lawn_sowing',
          'properties.p2o5_correction_lawn_sowing',
          'properties.k2o_correction_lawn_sowing',
          'properties.n_correction_harvest_residues',
          'properties.p2o5_correction_harvest_residues',
          'properties.k2o_correction_harvest_residues',
          'properties.n_correction_precursor_effect',
          'properties.produced',
          'properties.purchased',
          'properties.recycled',
          'active',
          'properties.record_type',
        ];
        this.hiddenTableColumns = [
          'record_type',
          'n_correction_lawn_sowing',
          'p2o5_correction_lawn_sowing',
          'k2o_correction_lawn_sowing',
          'n_correction_harvest_residues',
          'p2o5_correction_harvest_residues',
          'k2o_correction_harvest_residues',
          'n_correction_precursor_effect',
        ];
        break;
      case 'machines_and_aggregates':
      case 'self_propelled_machinery':
        this.columnsOrder = ['value', 'properties.width_and_unit', 'active'];
        break;
      case 'product_types':
        this.columnsOrder = [
          'properties.sector',
          'value',
          'properties.produced',
          'properties.purchased',
          'properties.recycled',
          'properties.units_of_measure',
          'properties.veterinary_medicinal_product',
          'properties.base_humidity',
          'properties.dry_matter',
          'properties.feedingstuff_eat_losses_perc',
          'properties.feedingstuff_storage_losses_perc',
          'properties.natural_farms_dry_matter_restriction_perc',
          'active',
        ];
        this.hiddenTableColumns = [
          'feedingstuff_eat_losses_perc',
          'feedingstuff_storage_losses_perc',
          'natural_farms_dry_matter_restriction_perc',
        ];
        //if (this.classifier.parent_id !== null) {
        this.prefillableFields = [ClassifierPropertyKey.SECTOR];
        //}
        break;
      case 'soil_granulometric_composition':
        this.columnsOrder = [
          'value',
          'properties.abbreviation',
          'properties.n_correction_granulometric_composition',
          'properties.p2o5_correction_granulometric_composition',
          'properties.k2o_correction_granulometric_composition',
          'active',
        ];
        break;
      case 'plant_development_stages':
        this.columnsOrder = [
          'properties.plant_development_stage_category',
          'value',
          'properties.plant_development_stage_description',
          'active',
        ];
        this.classifierValueTitle = 'classifiers.plant_development_stage';
        break;
      case 'crop_works':
        this.columnsOrder = [
          'properties.crop_work_type',
          'value',
          'properties.delete_field_crop',
          'properties.update_field_crop',
          'properties.choose_category_for_spraying',
          'active',
          'properties.record_type',
        ];
        this.hiddenTableColumns = ['record_type'];
        break;
      case 'plant_protection_products':
        this.columnsOrder = [
          'properties.plant_protection_product_type',
          'value',
          'active',
        ];
        break;
      case 'fertilizers':
        this.columnsOrder = [
          'properties.fertilizer_type',
          'properties.crop_work_type',
          'value',
          'properties.n',
          'properties.p2o5',
          'properties.k2o',
          'properties.ca',
          'properties.mg',
          'properties.na',
          'properties.s',
          'properties.b',
          'properties.co',
          'properties.cu',
          'properties.fe',
          'properties.mn',
          'properties.mo',
          'properties.zn',
          'properties.caco3',
          'properties.harvest_per_year',
          'properties.dry_matter_percentage',
          'active',
        ];
        this.hiddenTableColumns = [
          'record_type',
          'mg',
          's',
          'b',
          'co',
          'cu',
          'fe',
          'mn',
          'mo',
          'zn',
          'caco3',
        ];
        break;
      case 'crop_production_models':
        this.columnsOrder = ['properties.crop_group', 'value', 'active'];
        break;
      case 'livestock_production_models':
        this.columnsOrder = ['properties.livestock_sector', 'value', 'active'];
        break;
      case 'animals':
        this.columnsOrder = [
          'properties.animal_species_code',
          'properties.animal_species',
          'properties.animal_type',
          'properties.animal_group_code',
          'value',
          'properties.animal_gender',
          'properties.animal_dry_matter',
          'active',
        ];
        this.classifierValueTitle = 'classifiers.animal_group';
        break;
      case 'animal_species':
        this.columnsOrder = [
          'properties.animal_species_code',
          'value',
          'properties.animal_species_category',
          'properties.animal_dry_matter',
          'active',
        ];
        this.classifierValueTitle = 'classifiers.animal_species_name';
        break;
      case 'animal_categories':
        this.columnsOrder = [
          'properties.animal_species_code',
          'properties.animal_category_abbreviation',
          'properties.animal_species',
          'properties.animal_type',
          'value',
          'properties.animal_gender',
          'properties.animal_dry_matter',
          'active',
        ];
        this.classifierValueTitle = 'classifiers.animal_category_name';
        break;
      case 'manure':
        this.columnsOrder = [
          'properties.fertilizer_type',
          'properties.fertilizer',
          'value',
          'properties.year',
          'properties.n_correction_manure',
          'properties.p2o5_correction_manure',
          'properties.k2o_correction_manure',
          'active',
        ];
        break;
      case 'correction_by_green_manure_type':
        this.columnsOrder = ['value', 'properties.n_correction', 'active'];
        break;
      case 'correction_by_organic_matter_in_soil':
        this.columnsOrder = [
          'properties.plant_food_element',
          'value',
          'properties.organic_matter_soil_content_percent_from',
          'properties.organic_matter_soil_content_percent_to',
          'properties.correction_coefficient',
          'active',
        ];
        break;
      case 'max_norms_and_correction_factors_for_planned_harvest':
        this.columnsOrder = [
          'properties.crop',
          'value',
          'properties.yield_level_from_t_ha1',
          'properties.yield_level_to_t_ha1',
          'properties.maximum_permissible_amount_of_nitrogen_kg_ha1',
          'properties.standard_crop_t_ha',
          'properties.n_need_for_standard_harvest_kg_ha',
          'properties.p2o5_need_for_standard_harvest_kg_ha',
          'properties.k2o_need_for_standard_harvest_kg_ha',
          'properties.n_correction_for_each_ton_of_yield_deviation_from_the_standard_yield_kg_ha',
          'properties.p2o5_correction_for_each_ton_of_yield_deviation_from_the_standard_yield_kg_ha',
          'properties.k2o_correction_for_each_ton_of_yield_deviation_from_the_standard_yield_kg_ha',
          'active',
        ];
        break;
      case 'correction_by_soil_ph_kcl':
        this.columnsOrder = [
          'properties.granulometric_composition_of_the_soil',
          'value',
          'properties.soil_acidity_ph_kcl_from',
          'properties.soil_acidity_ph_kcl_to',
          'properties.n',
          'properties.p2o5',
          'properties.k2o',
          'active',
        ];

        break;
      case 'correction_for_phosphorus_and_potassium_content_in_the_soil':
        this.columnsOrder = [
          'properties.plant_food_element',
          'properties.content_in_soil',
          'value',
          'properties.content_in_soil_from_mg_kg',
          'properties.content_in_soil_up_to_mg_kg',
          'properties.correction_factor',
          'active',
        ];
        break;
      case 'time_of_use':
        this.columnsOrder = ['value', 'properties.period', 'active'];
        this.classifierValueTitle = 'classifiers.month';
        break;
    }
  }

  /**
   * Function returns dynamically generated yup validation schema for form components
   */
  public getYupSchema() {
    let schema = {
      value: yup.string().required(),
      active: yup.boolean(),
      properties: yup.object(),
    };

    let rules: any = {};

    Object.values(this.classifier.properties).forEach((property) => {
      const additionalValidationRules =
        property?.options?.additional_validation_rules ?? {};

      switch (property.type) {
        case FieldTypes.INTEGER:
        case FieldTypes.DECIMAL:
          let rule;

          const {
            allow_negative: negativeAllowed = false,
            allow_zero: zeroAllowed = false,
          } = additionalValidationRules;

          switch (true) {
            case negativeAllowed && zeroAllowed:
              rule = yup.number().nullable().transform(numberTransform);
              break;
            case !negativeAllowed && zeroAllowed:
              rule = yup.number().min(0).nullable().transform(numberTransform);
              break;
            case negativeAllowed && !zeroAllowed:
              rule = yup
                .number()
                .nullable()
                .test(
                  `properties.${property.id}`,
                  this.t('Field must be either a negative or positive number'),
                  (value, context) => {
                    return value !== 0;
                  }
                )
                .transform(numberTransform);
              break;
            default:
              rule = yup
                .number()
                .positive()
                .nullable()
                .transform(numberTransform);
              break;
          }

          rules[property.id] = rule;
          break;
        case FieldTypes.SELECT_VALUE:
        case FieldTypes.SELECT_RELATION:
          rules[property.id] = yup.string().nullable();
          break;
        case FieldTypes.MULTI_SELECT_VALUE:
        case FieldTypes.MULTI_SELECT_RELATION:
          rules[property.id] = yup.array().nullable();
          break;
        case FieldTypes.TEXT:
          rules[property.id] = yup.string().nullable();
          break;
        case FieldTypes.BOOLEAN:
          rules[property.id] = yup.boolean();

          if (property.default === null) {
            rules[property.id] = rules[property.id].nullable();
          } else if (typeof property.default === FieldTypes.BOOLEAN) {
            rules[property.id] = rules[property.id].default(property.default);
          }

          break;
        case FieldTypes.FILE:
          rules[property.id] = yup.array().of(
            yup.object().shape({
              id: yup.string().required().uuid(),
            })
          );
          break;
        case FieldTypes.FILES:
          // TODO: add when there is first use case
          break;
        case FieldTypes.READ_ONLY:
          break;
      }

      if (property.required) {
        switch (property.type) {
          case FieldTypes.INTEGER:
          case FieldTypes.DECIMAL:
          case FieldTypes.TEXT:
          case FieldTypes.BOOLEAN:
          case FieldTypes.SELECT_VALUE:
          case FieldTypes.SELECT_RELATION:
            rules[property.id] = rules[property.id].required();
            break;
          case FieldTypes.MULTI_SELECT_VALUE:
          case FieldTypes.MULTI_SELECT_RELATION:
            rules[property.id] = rules[property.id].required().min(1);
            break;
          case FieldTypes.FILE:
            rules[property.id] = rules[property.id].required().min(1);
            break;
          case FieldTypes.FILES:
            // TODO: add when there is first use case
            break;
        }
      } else {
        switch (property.type) {
          case FieldTypes.FILE:
            rules[property.id] = rules[property.id].nullable();
            break;
        }
      }
    });

    schema.properties = yup.object().shape(rules);

    return yup.object().shape(schema);
  }

  /**
   * Funtion returns dynamically generated array of react form components
   *
   * @returns FormComponent[]
   */
  public async getFormFields(): Promise<FormComponent[]> {
    return new Promise(async (resolve, reject) => {
      let elements = [];

      elements.push({
        element: Text('value', this.classifierValueTitle),
        order: this.getIndex('value'),
      });

      for (const [, property] of Object.entries(this.classifier.properties)) {
        let accessPath = 'properties.' + property.key;
        let index = this.getIndex(accessPath);
        let name = `properties.${property.id}`;

        switch (property.type) {
          case FieldTypes.BOOLEAN:
            elements.push({
              element: Checkbox(name, property.name),
              order: index,
            });
            break;
          case FieldTypes.SELECT_VALUE:
          case FieldTypes.MULTI_SELECT_VALUE:
          case FieldTypes.SELECT_RELATION:
          case FieldTypes.MULTI_SELECT_RELATION:
            let propertyOptions = property.options as ClassifierPropertyOptions;
            let selectOptions: Option[] = [];
            if (propertyOptions !== null && this.readonly === false) {
              switch (true) {
                case propertyOptions.hasOwnProperty('related_classifier'):
                  let classifier = propertyOptions.related_classifier;
                  let values;
                  if (classifier.hasOwnProperty('parentId')) {
                    values = this.classifierService.allValuesByParentValueId(
                      classifier.group,
                      classifier.parentId
                    );
                  } else {
                    values = this.classifierService.allValues(
                      classifier.group,
                      classifier.id
                    );
                  }
                  const classifierValues = await values.toPromise();
                  classifierValues.forEach(
                    (classifierValue: ClassifierValue) => {
                      selectOptions.push({
                        label: classifierValue.value,
                        value: classifierValue.id,
                      });
                    }
                  );
                  break;
                case propertyOptions.hasOwnProperty('values'):
                  Object.values(propertyOptions.values).forEach((value) => {
                    selectOptions.push({
                      label: this.t(
                        'classifiers.properties.' + property.key + '.' + value
                      ),
                      value: value,
                    });
                  });
                  break;
              }
            }
            elements.push({
              element: Select(name, {
                options: selectOptions,
                multi:
                  [
                    FieldTypes.MULTI_SELECT_VALUE,
                    FieldTypes.MULTI_SELECT_RELATION,
                  ].indexOf(property.type) !== -1,
                label: property.name,
              }),
              order: index,
            });
            break;
          case FieldTypes.INTEGER:
            {
              const negativeAllowed =
                property?.options?.additional_validation_rules
                  ?.allow_negative ?? false;

              elements.push({
                element: Integer(name, property.name, null, negativeAllowed),
                order: index,
              });
            }
            break;
          case FieldTypes.DECIMAL:
            {
              const negativeAllowed =
                property?.options?.additional_validation_rules
                  ?.allow_negative ?? false;

              elements.push({
                element: Decimal(name, property.name, null, negativeAllowed),
                order: index,
              });
            }
            break;
          case FieldTypes.READ_ONLY:
            elements.push({
              element: Disabled(Text(name, property.name)),
              order: index,
            });
            break;
          case FieldTypes.FILE:
            elements.push({
              element: File(name, property.name, false),
              order: index,
            });
            break;
          case FieldTypes.FILES:
            elements.push({
              element: File(name, property.name, true),
              order: index,
            });
            break;
          case FieldTypes.TEXT:
          default:
            elements.push({ element: Text(name, property.name), order: index });
            break;
        }
      }

      elements.push({
        element: Checkbox('active', 'active'),
        order: this.getIndex('active'),
      });

      resolve(elements.sort((a, b) => a.order - b.order).map((x) => x.element));
    });
  }

  /**
   * Function returns ordered and translated table column data
   * @returns Array<{}> of column objects
   * @param except
   */
  public getTableColumns(): Array<{}> {
    let cols = [];

    cols.push({
      accessor: 'value',
      header: this.t(this.classifierValueTitle),
      dataType: FieldTypes.TEXT,
      sortable: false,
      order: this.getIndex('value'),
    });

    //push dynamic properties columns
    Object.entries(this.classifier.properties).forEach(([id, property]) => {
      let index = this.getIndex('properties.' + property.key);

      if (index >= 0 && !this.hiddenTableColumns.includes(property.key)) {
        cols.push({
          accessor: 'properties.' + id + '.value',
          translatable:
            ClassifierManager.conditionallyTranslatablePropertyTypes().includes(
              property.type
            )
              ? (key: string) => this.t(key)
              : false,
          translationPrefix: 'classifiers.properties.' + property.key + '.',
          header: this.t('classifiers.' + property.key),
          dataType: property.type,
          sortable: false,
          order: index,
        });
      }
    });

    cols.push({
      accessor: 'active',
      header: this.t('active'),
      dataType: FieldTypes.BOOLEAN,
      sortable: false,
      order: this.getIndex('active'),
    });

    return cols.sort((a, b) => a.order - b.order);
  }

  /**
   * getClickRoute
   */
  public static getClickRoute(classifier: Classifier): any {
    let id = classifier.id;
    let key = classifier.key;
    let groupKey = classifier.group.key;
    let clickRoute = route(INTERNAL_CLASSIFIER_VALUE_EDIT, {
      classifierId: id,
      groupKey: groupKey,
    });

    switch (key) {
      case 'product_categories_and_types':
      case 'obtained_product_categories_and_types':
        clickRoute = route(INTERNAL_CLASSIFIER_VALUE_VIEW, {
          classifierId: id,
          groupKey: groupKey,
        });
        break;
      case 'product_types':
      case 'obtained_product_types':
        clickRoute = route(INTERNAL_CLASSIFIER_VALUE_EDIT, {
          classifierId: id,
          groupKey: groupKey,
        });
        break;
    }
    return clickRoute;
  }

  /**
   * getPropertyValues
   */
  public getPropertyValues(
    classifierValue?: ClassifierValue,
    shouldTranslate: boolean = false
  ): any {
    const mappedProperties = {} as any;

    Object.entries(this.classifier.properties).forEach(([key, property]) => {
      let propertyValue = classifierValue.hasOwnProperty('properties')
        ? classifierValue.properties[key]
        : false;
      let value: any = propertyValue
        ? propertyValue.value
        : this.prefillableFields.indexOf(property.key) !== -1
        ? property.options.values
        : property.default;

      if (
        (shouldTranslate &&
          ClassifierManager.conditionallyTranslatablePropertyTypes().includes(
            property.type
          )) ||
        ClassifierManager.permanentlyTranslatablePropertyTypes().includes(
          property.type
        )
      ) {
        switch (property.type) {
          case FieldTypes.SELECT_RELATION:
            value = value[0];
            break;
          case FieldTypes.MULTI_SELECT_RELATION:
            value = value.map((item: any) => this.t(item.label)).sort();
            break;
          case FieldTypes.MULTI_SELECT_VALUE:
          case FieldTypes.SELECT_VALUE:
            value = value
              .map((item: any) => {
                return { id: item, label: this.t(item) };
              })
              .sort();
            break;
          case FieldTypes.DECIMAL:
          case FieldTypes.INTEGER:
          case FieldTypes.TEXT:
          default:
            value = this.t(value);
            break;
        }
      } else {
        if (
          property.options &&
          property.options.hasOwnProperty('related_classifier') &&
          value
        ) {
          switch (property.type) {
            case FieldTypes.SELECT_RELATION:
              value = value[0].id;
              break;
            default:
              let ids: string[] = [];
              Object.values(value as Object).forEach((v) => {
                ids.push(v.id);
              });
              value = ids;
              break;
          }
        }
      }

      mappedProperties[property.id] = value;
    });

    return mappedProperties;
  }

  /**
   * getPropertyBykey
   */
  public getPropertyByKey(key: string): ClassifierProperty {
    let filteredProperties = Object.values(this.classifier.properties).filter(
      (p) => p.key === key
    );

    return filteredProperties.length >> 0 ? filteredProperties[0] : null;
  }

  /**
   * getBackRoute
   */
  public static getBackRoute(
    classifier: Classifier,
    classifierValue?: ClassifierValue
  ): string {
    let groupKey = classifier.group.key;
    let backRoute = route(INTERNAL_CLASSIFIER_VALUES, {
      classifierId: classifier.id,
      groupKey: groupKey,
    });

    switch (classifier.key) {
      case 'product_categories_and_types':
      case 'obtained_product_categories_and_types':
        if (classifierValue) {
          backRoute = route(INTERNAL_CLASSIFIER_VALUE_VIEW, {
            id: classifierValue.id,
            classifierId: classifier.id,
            groupKey: groupKey,
          });
        }
        break;
      case 'product_types':
      case 'obtained_product_types':
        backRoute = route(INTERNAL_CLASSIFIER_VALUE_VIEW, {
          id: classifierValue.parent_id,
          classifierId: classifier.parent_id,
          groupKey: groupKey,
        });
        break;
    }
    return backRoute;
  }

  /**
   * Funtion returns index + 1 of array value if exists value if not then defaul is 1000
   * @returns number
   * @param value
   */
  private getIndex(value: string): number {
    let order = this.columnsOrder.indexOf(value);
    return order >= 0 ? order + 1 : order;
  }

  static conditionallyTranslatablePropertyTypes() {
    return conditionally_translatable_property_types;
  }

  static permanentlyTranslatablePropertyTypes() {
    return permanently_translatable_property_types;
  }
}
